<template>
  <div :style="{'--contrast-multiplier': contrastMultiplier}" class="contrast-filter"></div>
  <div :style="{'--font-scale': fontMultiplier, fontFamily: dyslexiaIsActive ? 'OpenDyslexic' : 'Poppins', '--image-filter': `hue-rotate(${imageFilter}deg)`}">
    <div
        v-if="loadingState"
        class="loader-container vw-100 vh-100 position-fixed d-flex align-items-center justify-content-center">
        <LoaderComponent/>
    </div>

    <div v-if="loadingView !== 'app'" :class="['vh-100 vw-100 position-relative d-flex']">
      <SideBarComponent v-if="renderClassicSidebar"/>
      <AcademicSidebar v-if="!renderClassicSidebar && renderSidebar" />

      <div :class="[getAppLayoutClass(), 'overflow-y-auto router-view-wrapper' ]">
        <div class="router-view-content">
          <BreadcrumbNav />
          <RouterView id="content-rendered"/>
        </div>
      </div>
      
      <AcessibilityItems/>
      <AvaliateMaterialsButton v-if="shouldRenderAvaliateMaterialsButton" />
      <transition name="slide-toast">
        <BadgeToast v-if="newBadgeState" />
      </transition>
      <ScrollToTopButton :scrollToTopState="scrollToTopState"/>
    </div>

    <AccessAsCard v-if="accessAsState" />
    <AccessAsPointerCard v-if="userWidth > 1024 && accessAsState && activeModalName !== 'disableAccessAsModal'" />
    <DisableAccessAsModal v-if="activeModalName === 'disableAccessAsModal'" />

    <div v-if="showSessionWarningModal" class="session-warning-backdrop">
      <div class="modal d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-centered modal-md" role="document">
          <div class="modal-content rounded-4">
            <div class="modal-header">
              <h5 class="modal-title">Ei, ainda esta ai?</h5>
              <button type="button" class="btn-close" aria-label="Fechar" @click="handleUserStillOnline"></button>
            </div>
            <div class="modal-body">
              <p class="mb-0">Sua sessao sera encerrada em {{ sessionWarningExpirationTime }} segundos se nao houver confirmacao.</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-primary" @click="handleUserStillOnline">Sim, estou online</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ColorCard v-if="selectName === 'ColorPicker'" />
  <MaterialAudioPlayerCard
      @mousedown="updateDraggingState"
      @mouseup="updateDraggingState"
      v-if="audioState"
  />
</template>

<script>
import {mapGetters, useStore} from 'vuex';
import 'vue3-toastify/dist/index.css';
import AcessibilityItems from "@/components/items/AcessibilityItems.vue";
import ScrollToTopButton from "@/components/buttons/ScrollToTopButton.vue";
import {useRoute, useRouter} from "vue-router";
import SideBarComponent from "@/components/sidebars/SidebarComponent.vue";
import LoaderComponent from "@/components/loaders/LoaderComponent.vue";
import store from "@/store";
import {computed, nextTick, ref} from "vue";
import {setApplicationColors} from "./utils/helpers";
import ColorCard from "./components/cards/ColorCard.vue";
import MaterialAudioPlayerCard from "./components/cards/MaterialAudioPlayerCard.vue";
import AccessAsCard from "./components/cards/AccessAsCard.vue";
import AccessAsPointerCard from "./components/cards/AccessAsPointerCard.vue";
import DisableAccessAsModal from "./components/modals/DisableAccessAsModal.vue";
import AvaliateMaterialsButton from './components/buttons/AvaliateMaterialsButton.vue';
import AcademicSidebar from './components/sidebars/AcademicSidebar.vue';
import BadgeToast from './components/CustomToast/BadgeToast.vue';
import BreadcrumbNav from './components/navs/BreadcrumbNav.vue';

export default {

  data() {
    return {
      store: useStore(),
      renderScrollToTopButton: computed(() => this.scrollViews.includes(this?.route?.name)),
      shouldRenderAvaliateMaterialsButton: computed(() => this.route.name === 'Discipline Material' 
        && (Array.isArray(this.materialsData) && this.materialsData.length > 0)
        && (this.userRole === 'student')
      ),
      renderSidebar: computed(() => !this.shouldNotHaveSideBar.includes(this.route.name)),
      renderClassicSidebar: computed(() => Boolean((!this.userBelongsToAcademicRoles 
      || (this.userBelongsToAcademicRoles && this.userWidth > 1024))
      && this.renderSidebar
      )),
      showAlertListState: false,
      token: computed(() => store.getters.getToken),
      logOutExpirationTime: 1200,
      maxLogOutExpirationTime: 1200,
      showSessionWarningModal: false,
      sessionWarningExpirationTime: 60,
      maxSessionWarningExpirationTime: 60,
      logOutIntervalId: null,
      draggingAudioPlayer: false,
      router: useRouter(),
      route: useRoute(),
      shouldNotInteractWidthLogOutTimer: ['Login', 'Forgot Password', 'Error', 'Not Found'],
      modalExceptionActions: ['InsertMaterialItem', 'ImportQuestionsModal', 'NewQuestionsSettings'],
      selectIdTargetExceptions: ['app'],
      shouldNotPingAndHaveAudioPlayer: ['Login', 'Forgot Password'],
      shouldResetAccesAsState: ['Login', 'Forgot Password', 'Access As'],
      shouldNotHaveSideBar: ['Login', 'Forgot Password', 'Error', 'Not Found', 'Access As'],
      scrollViews: ['Student DashBoard', 'Disciplines', 'Questionnaire', 'My personalized disciplines', 'Create Quiz', 'Discipline Material', 'My Disciplines', 
      'Questionnaire Feedback', 'Quizzes', 'Update Quiz', 'Disciplines Feedback'],
      accessAsPointerCardWidth: null
    }
  },

  components: {
    DisableAccessAsModal,
    AccessAsPointerCard,
    AccessAsCard,
    MaterialAudioPlayerCard,
    ColorCard,
    AcessibilityItems,
    LoaderComponent,
    SideBarComponent,
    ScrollToTopButton,
    AvaliateMaterialsButton,
    AcademicSidebar,
    BadgeToast,
    BreadcrumbNav
  },

  computed: {
    ...mapGetters({
      dyslexiaIsActive: 'acessibility/dyslexiaIsActive',
      contrastMultiplier: 'acessibility/contrastMultiplier',
      fontMultiplier: 'acessibility/fontMultiplier',
      colorBlindnessGetter: 'acessibility/colorBlindnessGetter',
      colorBlindnessParams: 'acessibility/colorBlindnessParams',
      imageFilter: 'acessibility/imageFilter',
      consumerSettings: 'getConsumerSettings',
      modalState: 'getModalState',
      modalWrapperDiv: "getModalWrapperDiv",
      formModalState: 'getFormModalState',
      activeModalName: 'getModalName',
      userEnteredSomethingInModal: 'getUserEnteredSomething',
      selectState: 'getSelectState',
      selectWrapperDiv: 'getSelectWrapper',
      selectName: 'getSelectName',
      materialItemDataToUpdate: 'getMaterialDataToUpdate',
      consumerColors: 'getConsumerColors',
      audioState: 'getAudioPlayerState',
      userWidth: 'getUserWidth',
      userValidatedFiles: 'getUserValidatedFiles',
      errorsInFileImportation: 'getErrorsInQuestionImportation',
      userIsEditingErrorQuestion: 'getUserIsEditingErrorQuestion',
      accessAsState: 'getAccessAsState',
      materialsData: 'getMaterialsData',
      userRole: 'getType',
      forumChannel: 'getReverbChannel',
      consumerName: 'getConsumerName',
      userBelongsToAcademicRoles: 'getUserBelongsToAcademicRoles',
      userId: 'getId',
      getAcademicRoles: 'getAcademicRoles',
      newBadgeState: 'getUserNewBadgeState',
      existsDataRelatedToProfileRoutment: 'getExistsDataRelatedToProfileRoutment'
    }),
    ...mapGetters(["isLoading", 'loadingState', 'loadingView']),
    ...mapGetters('scrollToTop', ['scrollToTopState']),
    ...mapGetters('toastfy', ['getDispatchToastWhenMountedState', 'getToastDataToDispatch', 'getDispatchToastBetweenRoutes'])
  },

  mounted() {
    this.initializeLocalFunctions()
    this.initializeStoresDispatch()
    this.initializeScriptsAndJSFunctions()
    this.initializeListeners()
  },

  watch: {
    token: {
      async handler() {
        await this.handleReverbConnection()
        this.requestNotificationsData()
        this.initiliazeUserReverbConnection()
      }, deep: true
    },

    accessAsState: {
      handler() {
        this.checkAccessAsPointerCardSize()
      }
    },

    fontMultiplier: {
      handler() {
        this.checkAccessAsPointerCardSize()
      }
    },

    'route.name'(newValue, oldValue) {
      this.checkToastfyBetweenRoutes()
      this.checkIfAudioPlayerCanBeRendered()
      this.checkProfileRoutmentState(newValue)
      this.resetLogOutTimer()

      if (this.selectName === 'ColorPicker') {
        this.store.dispatch('clearSelectData')
        this.store.dispatch('clearColorPickerData')
      }

      if(this.shouldResetAccesAsState.includes(newValue)) {
        this.store.dispatch('resetUserDataUserModule')
      }

      if (oldValue === 'Login' && newValue !== oldValue) {
        this.resetLogOutTimer()
        this.startTimeCounter()
      }
    },

    colorBlindnessGetter: {
      handler(newValue) {
        this.setSystemColors(newValue)
      },
      deep: true
    }

  },

  beforeUnmount() {
    this.stopListeners()
  },

  methods: {
    checkProfileRoutmentState(newRouteName) {
      const relatedToProfileView = ['Profile View', 'Profile Settings']
      if (!relatedToProfileView.includes(newRouteName) && this.existsDataRelatedToProfileRoutment) {
        this.store.dispatch('setDataRelatedToProfileRoutment')
      }
    },

    getAppLayoutClass() {
      if (!this.renderSidebar) return 'full-size-container'
      return this.renderClassicSidebar
      ? 'classic-sidebar-container'
      : 'academic-sidebar-container'
    },

    checkIfAudioPlayerCanBeRendered() {
      if(this.audioState) {
        if(this.shouldNotPingAndHaveAudioPlayer.includes(this.route.name)) {
          this.store.dispatch('setAudioData')
        }
      }
    },

    checkToastfyBetweenRoutes() {
      if(this.getDispatchToastBetweenRoutes) {
        switch (this.getToastDataToDispatch.type) {
          case 'success':
            this.store.dispatch('toastfy/activeSuccesNotification', { text: this.getToastDataToDispatch.message || "Sem Mensagem" })
            break;
          case 'error':
            this.store.dispatch('toastfy/activeErrorNotification', { text: this.getToastDataToDispatch.message || "Sem Mensagem" })
            break;
        }

        this.store.dispatch('toastfy/setToastDataToDispatchBetweenRoutes')
      }
    },

    checkAccessAsPointerCardSize() {
      if (this.accessAsState) {
        this.$nextTick(() => {
          const containerElement = document.querySelector('.pointer-container')
          if (containerElement) {
            const containerPosition = containerElement.getBoundingClientRect()
            this.accessAsPointerCardWidth = containerPosition.width
          }
        })
      }
    },

    checkToastDataWhenMounted() {
      if(this.getDispatchToastWhenMountedState) {
        switch (this.getToastDataToDispatch.type) {
          case 'success':
            this.store.dispatch('toastfy/activeSuccesNotification', { text: this.getToastDataToDispatch.message || "Sem Mensagem" })
            break;
          case 'error':
            this.store.dispatch('toastfy/activeErrorNotification', { text: this.getToastDataToDispatch.message || "Sem Mensagem" })
            break;
        }

        this.store.dispatch('toastfy/setToastDataToDispatchWhenMounted')
      }
    },

    checkIfUserDroppedAudioOutSideView(event) {
      if (this.draggingAudioPlayer && !event?.target?.classList?.contains('audio-name')) {
        this.draggingAudioPlayer = false
      }
    },

    updateDraggingState(event) {
      if (this.userWidth >= 1024) {
        this.draggingAudioPlayer = !this.draggingAudioPlayer
        if (this.draggingAudioPlayer) {
          const root = document.documentElement;
          root.style.mozUserSelect = 'none';
          root.style.msUserSelect = 'none';
          root.style.userSelect = 'none';
          return
        }

        const root = document.documentElement;
        root.style.mozUserSelect = 'auto';
        root.style.msUserSelect = 'auto';
        root.style.userSelect = 'auto';
      }
    },

    setSystemColors(colorBlindnessState) {
      if (colorBlindnessState.state) {
        setApplicationColors(this.colorBlindnessParams[colorBlindnessState.index])
        this.store.dispatch('editConsumerColors', this.colorBlindnessParams[colorBlindnessState.index])
        return
      }
      setApplicationColors({ primary_color: this.consumerSettings.original_primary_color, secondary_color: this.consumerSettings.original_secondary_color })
      this.store.dispatch('editConsumerColors',{primary_color: this.consumerSettings.original_primary_color, secondary_color: this.consumerSettings.original_secondary_color})
      this.store.dispatch('acessibility/resetContrastMultiplier')
    },

    startTimeCounter() {
      this.clearLogOutInterval()
      this.logOutIntervalId = setInterval(() => {
        if (this.shouldNotInteractWidthLogOutTimer.includes(this.route.name)) return

        if (this.showSessionWarningModal) {
          this.sessionWarningExpirationTime--
          if (this.sessionWarningExpirationTime <= 0) {
            this.logoutUser()
            return
          }
          return
        }

        this.logOutExpirationTime--
        if (this.logOutExpirationTime <= 0) {
          this.openSessionWarningModal()
          return
        }
      }, 1000)
    },

    clearLogOutInterval() {
      if (this.logOutIntervalId) {
        clearInterval(this.logOutIntervalId)
        this.logOutIntervalId = null
      }
    },

    openSessionWarningModal() {
      this.showSessionWarningModal = true
      this.sessionWarningExpirationTime = this.maxSessionWarningExpirationTime
    },

    async handleUserStillOnline() {
      this.showSessionWarningModal = false
      this.sessionWarningExpirationTime = this.maxSessionWarningExpirationTime
      this.logOutExpirationTime = this.maxLogOutExpirationTime

      if (this.token) {
        await this.$store.dispatch('pingUser', { userToken: this.token }).catch(() => {
          this.logoutUser()
        })
      }
    },

    logoutUser() {
      this.showSessionWarningModal = false
      this.sessionWarningExpirationTime = this.maxSessionWarningExpirationTime
      this.clearLogOutInterval()
      this.router.push({name: 'Login'});
      localStorage.removeItem('authToken');
    },

    checkUserInputsInModal() {
      if (this.modalState) {
        this.store.dispatch('setUserEnteredSomething')
      }
    },

    handleClickFunctions(event) {
      this.checkSelectState(event)
      this.checkModalState(event)
      this.resetLogOutTimer()
    },

    handleModalExceptions() {
      if (this.activeModalName === 'NewQuestionsSettings' && this.userIsEditingErrorQuestion) {
        setTimeout(() => {
          this.store.dispatch('setModalData', { modalName: 'ImportQuestionsModal' })
        }, 0)
        return
      }

      if (this.activeModalName === 'ImportQuestionsModal') {
        if (this.userValidatedFiles && this.errorsInFileImportation.length > 0) {
          const userConfirmed = window.confirm("Tem certeza de que deseja sair? Todas as informações não salvas serão perdidas!")
          if (!userConfirmed) return
        }

        if (this.userValidatedFiles) {
          this.store.dispatch("toastfy/activeSuccesNotification", { text: "Processo de importação finalizado!" })
        }

        this.store.dispatch('resetDataRelatedToImportation')
        this.store.dispatch('clearModalData')
        return
      }

      switch (this.modalWrapperDiv) {
        case '.quizzes-list-wrapper':
          this.store.dispatch('setModalData', { modalName: 'InsertMaterialItem', formModal: true })
          break;
        case '.modal-content':
          if(this.selectName === 'ColorPicker') return
          if (this.materialItemDataToUpdate) return this.store.dispatch('clearModalData')

          if (this.formModalState && this.userEnteredSomethingInModal) {
            const userConfirmed = window.confirm("Tem certeza de que deseja sair? Todas as informações não salvas serão perdidas!")
            if (!userConfirmed) return
          }

          this.store.dispatch('clearUserEnteredSomething')
          this.store.dispatch('setModalData', {
            modalName: 'CreateMaterialModal',
          });
          this.store.dispatch('setDefaultColor', this.consumerColors.primary_color)
          this.store.dispatch('setColorSelected', this.consumerColors.primary_color)
          break;
      }
    },

    checkSelectState(event) {
      if (this.selectState) {
        if(this.selectIdTargetExceptions.includes(event.target.id)) return

        const wrapper = document.querySelector(this.selectWrapperDiv)
        if (wrapper && wrapper.contains(event.target)) return

        this.store.dispatch('clearSelectData')
      }
    },

    checkModalState(event) {
      if (this.modalState) {
        const wrapper = document.querySelector(this.modalWrapperDiv)

        if (wrapper && wrapper.contains(event.target)) return

        if(this.modalExceptionActions.includes(this.activeModalName)) return this.handleModalExceptions()

        if (this.formModalState && this.userEnteredSomethingInModal) {
          const userConfirmed = window.confirm("Tem certeza de que deseja sair? Todas as informações não salvas serão perdidas!")
          if (!userConfirmed) return
        }

        this.store.dispatch('clearModalData')
      }
    },

    handleMouseMovementFunctions(event){
      this.handleAudioPlayerPosition(event)
      this.handleAccessAsPointerCardPosition(event)
    },

    handleAccessAsPointerCardPosition(event) {
      if(this.accessAsState && this.userWidth > 1024) {
        const yGap = 10
        const xGap = 15
        const xGapInLeft = 25
        const yGapInBottom = 40
        let  YPosition = event.clientY - yGap
        let  XPosition = event.clientX + xGap

        XPosition + this.accessAsPointerCardWidth > window.innerWidth && (XPosition -= (this.accessAsPointerCardWidth + xGapInLeft))
        YPosition < yGap && (YPosition = yGap)
        YPosition > (window.innerHeight - yGapInBottom) && (YPosition = window.innerHeight - yGapInBottom)

        this.store.dispatch('updateAccessAsPointerCardPosition', { top: YPosition, left: XPosition })
      }
    },

    async initializeReverbConnections() {
      const shouldInitializeReverb = process?.env?.VUE_APP_INITIALIZE_REVERB === 'TRUE'
      const notRunningInProduction = process?.env?.VUE_APP === 'localhost'

      if(shouldInitializeReverb) {
        await this.store.dispatch('initializeReverbInstances', { consumerToken: this.token, notRunningInProduction: notRunningInProduction })
        
        if(notRunningInProduction) {
          await this.store.dispatch('testReverbConnections', this.userId)
        }
      }
    },

    handleAudioPlayerPosition(event){
      if (this.draggingAudioPlayer) {
        const audioPlayerContainer = document.querySelector('.audio-player-container')
        if (audioPlayerContainer) {
          const audioPlayerPosition = audioPlayerContainer.getBoundingClientRect()
          const audioPlayerNewPosition = ref({ top: event.clientY, left: event.clientX })
          const minDistanceFromBorder = 20
          const halfWidthOfAudioContainer = audioPlayerPosition.width / 2

          audioPlayerNewPosition.value.left < minDistanceFromBorder + (halfWidthOfAudioContainer) && (audioPlayerNewPosition.value.left = minDistanceFromBorder + halfWidthOfAudioContainer)
          audioPlayerNewPosition.value.left + halfWidthOfAudioContainer > this.userWidth - minDistanceFromBorder && (audioPlayerNewPosition.value.left = this.userWidth - minDistanceFromBorder - halfWidthOfAudioContainer)
          audioPlayerNewPosition.value.top < minDistanceFromBorder && (audioPlayerNewPosition.value.top = minDistanceFromBorder)
          audioPlayerNewPosition.value.top + audioPlayerPosition.height > window.innerHeight - minDistanceFromBorder && (audioPlayerNewPosition.value.top = window.innerHeight - minDistanceFromBorder - audioPlayerPosition.height)

          audioPlayerNewPosition.value.left = `${audioPlayerNewPosition.value.left}px`
          audioPlayerNewPosition.value.top = `${audioPlayerNewPosition.value.top}px`
          this.store.dispatch('setAudioPlayerPosition', audioPlayerNewPosition.value)
        }

      }
    },

    resetLogOutTimer() {
      if (this.showSessionWarningModal) return

      if (!this.shouldNotInteractWidthLogOutTimer.includes(this.route.name) && this.token) {
        this.logOutExpirationTime = this.maxLogOutExpirationTime
      }
    },

    handleResize() {
      this.$store.dispatch('setUserWidth', window.innerWidth)
    },

    userPing() {
      setInterval(() => {
        if (!this.shouldNotPingAndHaveAudioPlayer.includes(this.route.name) && this.token) {
          this.$store.dispatch('pingUser', {userToken: this.token})
              .catch(() => {
                this.router.push({name: 'Login'});
              });
        }
      }, 60000);
    },

    handleScroll(event) {
      if (this.renderScrollToTopButton) {
        const routeViewWrapper = document.querySelector('.router-view-wrapper')
        if (routeViewWrapper && routeViewWrapper.scrollTop > 100) {
          this.$store.dispatch('scrollToTop/setScrollToTop', {
            scrollToTopState: true
          })
        } else {
       this.$store.dispatch('scrollToTop/setScrollToTop', {
          scrollToTopState: false
        })
        }
      }
    },

    requestNotificationsData() {
      if (this.token) {
      this.store.dispatch('getNotificationsData', { page: 1, perPage: 6, sort: 'created_at|desc' })
      this.store.dispatch('setRenderNotificationsInAllViews', this.userRole)
      this.store.dispatch('connectUserToNotificationsChannel', { userId: this.userId, consumerName: this.consumerName })
      return
      } 
    },

    async handleReverbConnection() {
      await this.store.dispatch('resetReverbInstance')

      if (this.token) {
        await this.initializeReverbConnections()
        return
      }
    },

    initiliazeUserReverbConnection() {
      if (!this.token || !this.userId) return
      this.store.dispatch('initiliazeUserReverbConnection', this.userId)
      this.store.dispatch('initGlobalChatNotificationsChannel', { userId: this.userId })
    },

    async initializeStoresDispatch() {
      await this.handleReverbConnection()
      this.requestNotificationsData()
      this.initiliazeUserReverbConnection()
      this.store.dispatch('setNotificationsPage', 1)
      this.store.dispatch('resetDataRelatedToImportation')
      this.store.dispatch('clearColorPickerData')
      this.store.dispatch('setAudioData')
      this.store.dispatch('clearModalData')
      this.store.dispatch('clearSelectData')
      this.store.dispatch('alerts/setShowAlertModalData', { state: false,  alertToShow: null})
      this.store.dispatch('scrollToTop/setScrollToTop', { scrollToTopState: false })
      this.store.dispatch('alerts/setShowAlertModalData', { state: false,  alertToShow: null})
      this.store.dispatch('scrollToTop/setScrollToTop', { scrollToTopState: false })
      this.store.dispatch('resetChats')
    },

    async initializeListeners() {
      window.addEventListener('scroll', this.handleScroll, { passive: true, capture: true });
      window.addEventListener('mouseup', this.checkIfUserDroppedAudioOutSideView)
      window.addEventListener('change', this.checkUserInputsInModal)
      window.addEventListener("input", this.checkUserInputsInModal);
      window.addEventListener('resize', this.handleResize)
      window.addEventListener("mousemove", this.handleMouseMovementFunctions);
      window.addEventListener("keypress", this.resetLogOutTimer);
      window.addEventListener("click", this.handleClickFunctions);
      window.addEventListener("touchstart", this.resetLogOutTimer);
      window.addEventListener('beforeunload', this.resetStatesInStores)
    },


    initializeLocalFunctions() {
      this.checkToastDataWhenMounted()
      this.checkAccessAsPointerCardSize()
      this.userPing()
      this.setSystemColors(this.colorBlindnessGetter)
      this.handleResize()
      if (!this.shouldNotInteractWidthLogOutTimer.includes(this.route.name)) {
        this.startTimeCounter()
      }
    },

    initializeScriptsAndJSFunctions() {
      window.googleTranslateElementInit = () => {
        if (!window.google?.translate?.TranslateElement) return

        try {
          new window.google.translate.TranslateElement({
            pageLanguage: "pt",
            includedLanguages: 'en,es',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
          }, 'google_translate_element')
        } catch (error) {
          console.warn('Falha ao inicializar Google Translate:', error)
        }
      };

      const existingScript = document.querySelector('script[data-google-translate-script="true"]')
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        script.defer = true;
        script.setAttribute('data-google-translate-script', 'true')
        script.onerror = (error) => {
          console.warn('Falha ao carregar script do Google Translate:', error)
        }
        document.head.appendChild(script);
      }

      document.documentElement.style.fontSize = `${16 * this.fontMultiplier}px`;
    },

    stopListeners() {
      this.clearLogOutInterval()
      window.removeEventListener('mouseup', this.checkIfUserDroppedAudioOutSideView)
      window.removeEventListener('change', this.checkUserInputsInModal)
      window.removeEventListener('input', this.checkUserInputsInModal)
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize)
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('mousemove', this.handleMouseMovementFunctions);
      window.removeEventListener('keypress', this.resetLogOutTimer);
      window.addEventListener("click", this.handleClickFunctions);
      window.removeEventListener('touchstart', this.resetLogOutTimer);
      window.removeEventListener('beforeunload', this.resetStatesInStores)
    },
  }
};
</script>

<style scoped>
.loader-container {
  z-index: 1060;
}

.contrast-filter {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0);
  backdrop-filter: saturate(var(--contrast-multiplier));
  z-index: 9999;
  pointer-events: none;
  transform: translateZ(0);
}

.full-size-container {
  width: 100vw;
  min-height: 100vh;
}

.classic-sidebar-container {
  width: 0px;
  flex-grow: 1;
  padding: 4% 2%;
}

.academic-sidebar-container {
  width: 100vw;
  height: calc(100vh - 50px);
  padding: 20px;
  scroll-padding-bottom: 20px; 
}

.slide-toast-enter-active, .slide-toast-leave-active {
    transition: transform 0.5s ease, opacity 0.5s ease;
}

.slide-toast-enter-from {
    transform: translateX(100%);
    opacity: 0; 
}

.slide-toast-leave-to {
    transform: translateX(100%);
    opacity: 0; 
}

.session-warning-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1200;
}


@media (max-width: 1280px) {
  .classic-sidebar-container {
    padding: 50px 2%;
  }
}

</style>
